// Dependencies
import React from 'react';

import { useDispatch, useSelector } from 'src/store/store';

// Internal Components
import LoadingScreen from 'src/components/LoadingScreen';
import { authUser } from 'src/store/slices/auth';
import { useRouter } from 'next/router';
import { INTERNAL_PATHS } from 'src/config';
import ProgressBar from 'src/components/ProgressBar';

type Props = {
  children: React.ReactNode;
};
export default function AuthGuard({ children }: Props) {
  // ====================================
  // Redux
  const isInitialized = useSelector((state) => state.auth.isInitialized);
  const requiredAction = useSelector((state) => state.auth.required);
  const isAuthorization = useSelector((state) => state.auth.isAuthorization);
  const dispatch = useDispatch();

  // ====================================
  // State
  const [openUrlPathname, setOpenUrlPathname] = React.useState<string>('');

  // ====================================
  // Hooks
  const router = useRouter();

  // ====================================
  // Effects
  // Load Preferences data
  React.useEffect(() => {
    if (!isInitialized) {
      dispatch(authUser());
    }
  }, [dispatch, isInitialized]);

  React.useEffect(() => {
    if (router && !router.isReady) {
      setOpenUrlPathname(router.pathname);
    }
  }, [router]);

  // Check if preferences are loading or if there is an error
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // Check if user is not authorized
  if (!isAuthorization) {
    let currentAsPath;
    if (router.pathname.startsWith('dashboard') || router.pathname.startsWith('/dashboard')) {
      currentAsPath = encodeURIComponent(Buffer.from(router.asPath).toString('base64'));
    }
    // Redirect to login page
    router.push({
      pathname: INTERNAL_PATHS.auth.login,
      query: {
        callback: currentAsPath || undefined,
      },
    });
    return <LoadingScreen />;
  }

  // Check if user is authorized but action required
  if (isAuthorization && !!requiredAction) {
    // Final distention
    let finalDestination = '';
    if (requiredAction === 'email-verification') {
      if (router.pathname !== INTERNAL_PATHS.auth.verify) {
        finalDestination = INTERNAL_PATHS.auth.verify;
      }
    }
    if (requiredAction === 'phone-verification') {
      if (router.pathname !== INTERNAL_PATHS.auth.phoneVerify) {
        finalDestination = INTERNAL_PATHS.auth.phoneVerify;
      }
    }
    if (requiredAction === 'profile') {
      if (router.pathname !== INTERNAL_PATHS.auth.profile) {
        finalDestination = INTERNAL_PATHS.auth.profile;
      }
    }
    if (requiredAction === 'mfa') {
      if (
        router.pathname !== INTERNAL_PATHS.auth.mfa &&
        router.pathname !== INTERNAL_PATHS.auth.resetMfa
      ) {
        finalDestination = INTERNAL_PATHS.auth.mfa;
      }
    }

    if (requiredAction === 'address') {
      if (router.pathname !== INTERNAL_PATHS.auth.address) {
        finalDestination = INTERNAL_PATHS.auth.address;
      }
    }

    if (requiredAction === 'reset-password-mfa') {
      if (
        router.pathname !== INTERNAL_PATHS.auth.resetPasswordMfa &&
        router.pathname !== INTERNAL_PATHS.auth.resetMfa
      ) {
        finalDestination = INTERNAL_PATHS.auth.resetPasswordMfa;
      }
    }

    if (requiredAction === 'reset-password') {
      if (router.pathname !== INTERNAL_PATHS.auth.resetPassword) {
        finalDestination = INTERNAL_PATHS.auth.resetPassword;
      }
    }

    // Check from property
    if (finalDestination) {
      let currentAsPath;
      if (router.pathname.startsWith('dashboard') || router.pathname.startsWith('/dashboard')) {
        currentAsPath = encodeURIComponent(Buffer.from(router.asPath).toString('base64'));
        router.push({
          pathname: finalDestination,
          query: {
            callback: currentAsPath,
          },
        });
        return <LoadingScreen />;
      } else if (openUrlPathname === router.pathname) {
        router.push({
          pathname: finalDestination,
          query: router.query.callback
            ? {
                callback: router.query.callback || undefined,
              }
            : undefined,
        });
        return <LoadingScreen />;
      } else {
        router.push({
          pathname: finalDestination,
          query: router.query.callback
            ? {
                callback: router.query.callback || undefined,
              }
            : undefined,
        });
      }
    }
  }

  // Check if user is authorized and no action required
  if (isAuthorization && !requiredAction) {
    // Final distention
    const finalDestination = '/dashboard';

    let redirectMe = false;

    // If user trying to access one of action required pages
    if (
      [
        INTERNAL_PATHS.auth.verify,
        INTERNAL_PATHS.auth.phoneVerify,
        INTERNAL_PATHS.auth.profile,
        INTERNAL_PATHS.auth.mfa,
        INTERNAL_PATHS.auth.resetMfa,
        INTERNAL_PATHS.auth.address,
        INTERNAL_PATHS.auth.resetPasswordMfa,
        INTERNAL_PATHS.auth.resetPassword,
      ].includes(router.pathname)
    ) {
      redirectMe = true;
    }

    // Check from property
    if (redirectMe) {
      if (router.query?.callback && typeof router.query?.callback === 'string') {
        // Define variables
        let decodedCallbackValue = '';
        // Decode and parse from property
        try {
          decodedCallbackValue = Buffer.from(
            decodeURIComponent(router.query.callback),
            'base64'
          ).toString('utf-8');
        } catch (e) {}
        if (decodedCallbackValue) {
          // Check if callback value is to dashboard
          if (
            decodedCallbackValue.startsWith('/dashboard') ||
            decodedCallbackValue.startsWith('dashboard')
          ) {
            // Redirect to callback
            router.push(decodedCallbackValue);
          } else {
            router.push(finalDestination);
          }
        } else {
          router.push(finalDestination);
        }
      } else {
        router.push(finalDestination);
      }
    }
  }

  return <ProgressBar>{children}</ProgressBar>;
}
